import { useState } from "react";
import { AlertTriangle, CheckCircle2, ExternalLink, Wallet } from "lucide-react";
import { useAdminData } from "../../../application/hooks/useAdminData";
import { adminApi } from "../../../infrastructure/adminApi";

/**
 * Conectar la wallet del comercio.
 *
 * Existe porque hasta ahora el alta de wallet era **sólo por API**: un comercio real no podía
 * cobrar con bitcoin sin que alguien le corriera un curl. Este es el paso que faltaba para que
 * el onboarding sea autoservicio.
 *
 * La cadena de conexión es un secreto, así que: el campo no autocompleta, se limpia al
 * conectar, y el backend nunca la devuelve — de la wallet conectada sólo se muestra el relay,
 * que alcanza para reconocerla.
 *
 * Lexe: cada comercio pega la credencial de SU nodo (sólo recibir + leer). Ya no existe el
 * botón "Usar el nodo del servidor": cobraba al nodo del dueño del servidor, o sea que el
 * servidor custodiaba fondos ajenos. Los tenants que quedaron ahí ven un aviso para migrar.
 */
export default function AdminWalletPage() {
  // Se reusa el hook del panel en vez de un efecto propio: hace los setState DESPUÉS del
  // await y cancela al desmontar, que es lo que el lint de este repo exige (y con razón).
  const { data: estado, error, setError, reload } = useAdminData(adminApi.wallet.status);
  const [cadena, setCadena] = useState("");
  const [credencial, setCredencial] = useState("");
  const [ok, setOk] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const conectarNwc = async (e: React.FormEvent) => {
    e.preventDefault();
    const valor = cadena.trim();
    if (!valor) return;
    setBusy(true);
    setError("");
    setOk(null);
    try {
      const r = await adminApi.wallet.connectNwc(valor);
      setCadena(""); // no dejar el secreto en el DOM más de lo necesario
      setOk(
        r.payment_methods.includes("lightning")
          ? "Wallet conectada. Ya podés cobrar con bitcoin."
          : "Wallet guardada, pero el cobro con bitcoin sigue deshabilitado en este servidor.",
      );
      reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo conectar la wallet.");
    } finally {
      setBusy(false);
    }
  };

  const conectarLexe = async (e: React.FormEvent) => {
    e.preventDefault();
    const valor = credencial.trim();
    if (!valor) return;
    setBusy(true);
    setError("");
    setOk(null);
    try {
      await adminApi.wallet.connectLexe(valor);
      setCredencial(""); // igual que NWC: el secreto no se queda en el DOM
      setOk("Nodo Lexe conectado. Los cobros llegan directo a tu nodo.");
      reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo conectar Lexe.");
    } finally {
      setBusy(false);
    }
  };

  const lightning = estado?.payment_methods.includes("lightning") ?? false;
  const lexeRechazada = estado?.provider === "lexe" && estado.lexe_ok === false;
  const lexeServidor = estado?.provider === "lexe" && estado.lexe_mode === "server";
  const vence = estado?.lexe_expires_at
    ? new Date(estado.lexe_expires_at * 1000).toLocaleDateString("es-MX", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : null;

  return (
    <div>
      <h1 className="mb-4 text-2xl font-bold">Wallet</h1>

      {error && (
        <p data-testid="admin-wallet-error" className="mb-4 rounded-lg bg-error/10 px-4 py-2 text-sm text-error">
          {error}
        </p>
      )}
      {ok && (
        <p data-testid="admin-wallet-ok" className="mb-4 rounded-lg bg-success/10 px-4 py-2 text-sm text-success">
          {ok}
        </p>
      )}

      {/* Estado actual */}
      <div className="mb-6 rounded-lg border border-border-default bg-bg-surface p-4">
        <div className="flex items-start gap-3">
          <div className={lexeRechazada ? "text-error" : lightning ? "text-success" : "text-text-secondary"}>
            {lexeRechazada ? (
              <AlertTriangle size={22} />
            ) : lightning ? (
              <CheckCircle2 size={22} />
            ) : (
              <Wallet size={22} />
            )}
          </div>
          <div>
            <p data-testid="admin-wallet-state" className="font-medium">
              {lexeRechazada
                ? "Lexe rechaza tu credencial: conectá una nueva"
                : lightning
                  ? "Cobrando con bitcoin"
                  : "Sin wallet conectada"}
            </p>
            <p className="mt-1 text-sm text-text-secondary">
              {lightning ? (
                <>
                  Proveedor: <span className="text-text-primary">{estado?.provider}</span>
                  {estado?.relay && (
                    <>
                      {" · relay "}
                      <span className="font-mono text-xs text-text-primary">{estado.relay}</span>
                    </>
                  )}
                  {estado?.lexe_mode === "own" && estado.lexe_label && (
                    <>
                      {" · credencial "}
                      <span className="text-text-primary">{estado.lexe_label}</span>
                    </>
                  )}
                  {estado?.lexe_mode === "own" && vence && <>{` · vence el ${vence}`}</>}
                </>
              ) : (
                "El cobro en efectivo funciona igual: no depende de ninguna wallet."
              )}
            </p>
          </div>
        </div>
      </div>

      {lexeServidor && (
        <p
          data-testid="admin-wallet-lexe-server"
          className="mb-6 rounded-lg bg-warning/10 px-4 py-3 text-sm text-text-primary"
        >
          Hoy cobrás con el nodo Lexe <b>del servidor</b>, no con el tuyo: tus cobros llegan a un
          nodo que no administrás. Conectá tu propio nodo Lexe abajo; esta opción ya no se ofrece.
        </p>
      )}

      {/* Conectar por NWC */}
      <form onSubmit={conectarNwc} className="mb-6 rounded-lg border border-border-default bg-bg-surface p-4">
        <h2 className="font-bold">Conectar tu wallet (recomendado)</h2>
        <p className="mt-1 text-sm text-text-secondary">
          Tu wallet, tus fondos: los cobros llegan directo a ella y nosotros nunca los tocamos.
          Necesitás una wallet compatible con <span className="font-medium">Nostr Wallet Connect</span>{" "}
          y que esté siempre en línea — una app de teléfono dormida no puede emitir cobros.
        </p>
        <p className="mt-2 rounded-lg bg-warning/10 px-3 py-2 text-sm text-text-primary">
          Al crear la conexión, dale permiso <b>sólo para crear cobros y ver pagos</b>. No le des
          permiso de pagar: nadie lo necesita para que cobres, y podés revocarla desde tu wallet
          cuando quieras.
        </p>
        <label className="mt-3 block text-sm text-text-secondary" htmlFor="nwc">
          Cadena de conexión
        </label>
        <input
          id="nwc"
          data-testid="admin-wallet-nwc-input"
          value={cadena}
          onChange={(e) => setCadena(e.target.value)}
          placeholder="nostr+walletconnect://..."
          autoComplete="off"
          spellCheck={false}
          className="mt-1 w-full rounded-lg border border-border-default bg-bg-primary px-3 py-2 font-mono text-sm outline-none focus:border-accent"
        />
        <div className="mt-3 flex items-center gap-3">
          <button
            type="submit"
            disabled={busy || !cadena.trim()}
            data-testid="admin-wallet-nwc-save"
            className="rounded-lg bg-accent px-4 py-2 text-sm font-bold text-bg-primary hover:bg-accent-hover disabled:opacity-50"
          >
            {busy ? "Conectando..." : "Conectar wallet"}
          </button>
          <a
            href="https://nwc.dev"
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-1 text-sm text-text-secondary hover:text-text-primary"
          >
            Wallets compatibles <ExternalLink size={13} />
          </a>
        </div>
      </form>

      {/* Nodo Lexe propio */}
      {estado?.lexe_available && (
        <form onSubmit={conectarLexe} className="rounded-lg border border-border-default bg-bg-surface p-4">
          <h2 className="font-bold">Conectar tu nodo Lexe</h2>
          <p className="mt-1 text-sm text-text-secondary">
            Si usás la app <span className="font-medium">Lexe</span>, los cobros llegan directo a tu
            nodo y nosotros nunca los tocamos. Tu nodo está siempre en línea aunque el teléfono duerma.
          </p>
          <ol className="mt-2 list-decimal space-y-1 pl-5 text-sm text-text-secondary">
            <li>En la app Lexe, abrí el menú lateral → <b>SDK clients</b> → <b>Create new client</b>.</li>
            <li>
              Dale permiso <b>sólo para Recibir (receive) y Leer (read)</b>. Si la credencial
              puede gastar, la rechazamos: nadie lo necesita para que cobres.
            </li>
            <li>Copiá la credencial y pegala acá.</li>
          </ol>
          <label className="mt-3 block text-sm text-text-secondary" htmlFor="lexe">
            Credencial de Lexe
          </label>
          <input
            id="lexe"
            type="password"
            data-testid="admin-wallet-lexe-input"
            value={credencial}
            onChange={(e) => setCredencial(e.target.value)}
            placeholder="Pegá la credencial del cliente SDK"
            autoComplete="off"
            spellCheck={false}
            className="mt-1 w-full rounded-lg border border-border-default bg-bg-primary px-3 py-2 font-mono text-sm outline-none focus:border-accent"
          />
          <button
            type="submit"
            disabled={busy || !credencial.trim()}
            data-testid="admin-wallet-lexe-save"
            className="mt-3 rounded-lg border border-border-default px-4 py-2 text-sm font-bold text-text-primary hover:bg-bg-surface-hover disabled:opacity-50"
          >
            {busy ? "Comprobando..." : "Conectar nodo Lexe"}
          </button>
        </form>
      )}
    </div>
  );
}
